import { useState, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { useAuth } from '../../hooks/useAuth';
import { getUsageSummary, type UsageSummary, type ModelBreakdown } from '../../api/usage';
import { SpendChart } from './SpendChart';

const MODEL_COLORS = [
  '#00d4ff', '#2d8cf0', '#00c853', '#ffab00', '#ff1744',
  '#a855f7', '#ec4899', '#14b8a6', '#f97316', '#6366f1',
];

const TIME_RANGES = [
  { label: '1d', days: 1 },
  { label: '3d', days: 3 },
  { label: '7d', days: 7 },
  { label: 'MTD', days: 0 }, // month-to-date, calculated below
  { label: '30d', days: 30 },
  { label: 'All', days: 365 },
];

function getMonthToDateDays(): number {
  return new Date().getDate();
}

export function UsageDashboard() {
  const { getJwtToken } = useAuth();

  const [usage, setUsage] = useState<UsageSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRange, setSelectedRange] = useState(4); // default 30d

  const fetchUsage = useCallback(async () => {
    setLoading(true);
    setError(null);
    const range = TIME_RANGES[selectedRange];
    const days = range.days === 0 ? getMonthToDateDays() : range.days;
    try {
      const token = await getJwtToken();
      if (!token) return;
      const data = await getUsageSummary(token, days);
      setUsage(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load usage');
    } finally {
      setLoading(false);
    }
  }, [getJwtToken, selectedRange]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  const handleExportCsv = async () => {
    try {
      const token = await getJwtToken();
      if (!token) return;
      const API_BASE = import.meta.env.VITE_API_URL ?? '';
      const res = await fetch(`${API_BASE}/api/mgmt/usage/export`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'flux-spend-report.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export CSV');
    }
  };

  const formatCurrency = (val: number) =>
    `$${(val ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const formatNumber = (val: number) => (val ?? 0).toLocaleString();

  // Build pie chart data from breakdown
  const pieData = (usage?.breakdown ?? []).map((b: ModelBreakdown) => ({
    name: b.model || 'Unknown',
    value: b.spend || 0,
  }));

  return (
    <div>
      <div className="view__header">
        <h1 className="view__title">Usage Dashboard</h1>
        <div className="view__actions">
          {TIME_RANGES.map((r, idx) => (
            <button
              key={r.label}
              type="button"
              className={`btn btn--sm ${idx === selectedRange ? 'btn--primary' : 'btn--secondary'}`}
              onClick={() => setSelectedRange(idx)}
            >
              {r.label}
            </button>
          ))}
          <span style={{ width: 1, height: 24, background: '#1e2d4a', margin: '0 4px' }} />
          <button
            className="btn btn--sm btn--secondary"
            onClick={handleExportCsv}
            type="button"
          >
            Export CSV
          </button>
          <button
            className="btn btn--sm btn--secondary"
            onClick={fetchUsage}
            disabled={loading}
            type="button"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert--error">
          <span>{error}</span>
          <button onClick={() => setError(null)} type="button">Dismiss</button>
        </div>
      )}

      {loading && !usage && (
        <div className="view__empty">Loading usage data...</div>
      )}

      {/* KPI Row */}
      {usage && (
        <div className="dashboard-kpis">
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value">{formatCurrency(usage.totalSpend)}</span>
            <span className="dashboard-kpi__label">Total Spend</span>
          </div>
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value">{formatNumber(usage.totalRequests)}</span>
            <span className="dashboard-kpi__label">Requests</span>
          </div>
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value">{formatNumber(usage.totalTokens)}</span>
            <span className="dashboard-kpi__label">Tokens</span>
          </div>
          <div className="dashboard-kpi">
            <span className="dashboard-kpi__value">{usage.period || '--'}</span>
            <span className="dashboard-kpi__label">Period</span>
          </div>
        </div>
      )}

      {/* Daily spend chart */}
      {usage?.dailySpend && usage.dailySpend.length > 0 && (
        <section className="view__section">
          <h2 className="view__section-title">Daily Spend</h2>
          <div className="chart-container">
            <SpendChart
              data={usage.dailySpend}
              title=""
              color="#00d4ff"
            />
          </div>
        </section>
      )}

      {/* Spend Forecast */}
      {usage?.dailySpend && usage.dailySpend.length > 0 && (() => {
        const ds = usage.dailySpend;
        // Use last 7 days of data (or all available if fewer)
        const recentDays = ds.slice(-7);
        const totalRecent = recentDays.reduce((sum, d) => sum + (d.spend || 0), 0);
        const avgDaily = totalRecent / recentDays.length;

        // Calculate remaining days in the current month
        const now = new Date();
        const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
        const remainingDays = lastDay - now.getDate();

        // Spend so far this month (sum of all daily spend entries in current month)
        const currentMonthSpend = ds
          .filter((d) => {
            const dDate = new Date(d.date);
            return dDate.getMonth() === now.getMonth() && dDate.getFullYear() === now.getFullYear();
          })
          .reduce((sum, d) => sum + (d.spend || 0), 0);

        const projected = currentMonthSpend + avgDaily * remainingDays;

        // Compare against total budget if we have one — use totalSpend as a rough proxy
        // Color code: green if well within range, yellow if approaching, red if exceeding
        const projTier = usage.totalSpend > 0 && projected > usage.totalSpend * 1.2
          ? 'red'
          : usage.totalSpend > 0 && projected > usage.totalSpend * 0.9
            ? 'yellow'
            : 'green';

        return (
          <section className="view__section">
            <h2 className="view__section-title">Forecast</h2>
            <div className="forecast-card">
              <div className="forecast-card__row">
                <span className="forecast-card__label">Avg daily spend (last {recentDays.length} days)</span>
                <span className="forecast-card__value">{formatCurrency(avgDaily)}</span>
              </div>
              <div className="forecast-card__row">
                <span className="forecast-card__label">Spend so far this month</span>
                <span className="forecast-card__value">{formatCurrency(currentMonthSpend)}</span>
              </div>
              <div className="forecast-card__row">
                <span className="forecast-card__label">Remaining days this month</span>
                <span className="forecast-card__value">{remainingDays}</span>
              </div>
              <div className="forecast-card__row">
                <span className="forecast-card__label">Projected monthly spend</span>
                <span className={`forecast-card__value forecast-card__value--${projTier}`}>
                  {formatCurrency(projected)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                Based on {formatCurrency(avgDaily)}/day average over the last {recentDays.length} day{recentDays.length !== 1 ? 's' : ''}
              </div>
            </div>
          </section>
        );
      })()}

      {/* Model breakdown bar chart */}
      {pieData.length > 0 && (
        <section className="view__section">
          <h2 className="view__section-title">Spend by Model</h2>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={Math.max(200, pieData.length * 40 + 40)}>
              <BarChart
                data={pieData.sort((a, b) => b.value - a.value)}
                layout="vertical"
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <XAxis
                  type="number"
                  tickFormatter={(v: number) => `$${v.toFixed(2)}`}
                  stroke="#64748b"
                  fontSize={11}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={200}
                  stroke="#64748b"
                  fontSize={11}
                  tick={{ fill: '#8b95a8' }}
                />
                <Tooltip
                  contentStyle={{
                    background: '#0c1526',
                    border: '1px solid #1e2d4a',
                    borderRadius: 6,
                    fontSize: 12,
                  }}
                  formatter={(value: any) => [`$${Number(value ?? 0).toFixed(4)}`, 'Spend']}
                />
                <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                  {pieData.map((_entry, idx) => (
                    <Cell key={idx} fill={MODEL_COLORS[idx % MODEL_COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </section>
      )}

      {/* Model breakdown table */}
      {usage?.breakdown && usage.breakdown.length > 0 && (
        <section className="view__section">
          <h2 className="view__section-title">Model Details</h2>
          <div className="data-table__wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th className="data-table__th">Model</th>
                  <th className="data-table__th">Spend</th>
                  <th className="data-table__th">Tokens</th>
                  <th className="data-table__th">Requests</th>
                </tr>
              </thead>
              <tbody>
                {usage.breakdown.map((b: ModelBreakdown, idx: number) => (
                  <tr key={idx} className="data-table__tr">
                    <td className="data-table__td">
                      <code>{b.model || 'Unknown'}</code>
                    </td>
                    <td className="data-table__td">
                      {formatCurrency(b.spend || 0)}
                    </td>
                    <td className="data-table__td">
                      {formatNumber(b.tokens || 0)}
                    </td>
                    <td className="data-table__td">
                      {formatNumber(b.requests || 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}
